import { TextTooltip } from '../text-tooltip';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { InteractSettingsIcon, ViewErrorIcon, ViewHitIcon, ViewWarnIcon } from '@/client/assets';
import { Popover, PopoverContent, PopoverTrigger } from '@/client/components/ui/popover';
import { OPEN_SLIPPAGE } from '@/client/confs/app';
import { useTradeSettingStore } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { handleKeyDownDisableNumber } from '@/client/utils/handle';
import { deadlineSchema, slippageSchema } from '@/client/utils/schema';
import { ChangeEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const TradeSettings = ({ hideDeadline = false }: { hideDeadline?: boolean } = {}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const deadline = useTradeSettingStore(state => state.deadline);
  const slippage = useTradeSettingStore(state => state.slippage);
  const isAutoSlippage = useTradeSettingStore(state => state.isAutoSlippage);
  const autoSlippageDisplay = useTradeSettingStore(state => state.autoSlippageDisplay);
  const setDeadline = useTradeSettingStore(state => state.setDeadline);
  const setSlippage = useTradeSettingStore(state => state.setSlippage);
  const setIsAutoSlippage = useTradeSettingStore(state => state.setIsAutoSlippage);
  const [inputSlippage, setInputSlippage] = useState<string>(isAutoSlippage ? '' : slippage.toString());
  const [inputDeadline, setInputDeadline] = useState<string>(deadline.toString());

  // Display slippage: auto mode shows computed value, manual shows user input
  const displaySlippage = isAutoSlippage ? autoSlippageDisplay : slippage;
  const isHighSlippage = useMemo(
    () => !isAutoSlippage && displaySlippage > 5 && displaySlippage < 50,
    [isAutoSlippage, displaySlippage]
  );
  const isVeryHighSlippage = useMemo(() => !isAutoSlippage && displaySlippage >= 50, [isAutoSlippage, displaySlippage]);
  const numDeadline = useMemo(() => Number(inputDeadline || deadline), [deadline, inputDeadline]);
  const maxDeadline = 1 * 24 * 60;
  const isMaxDeadline = useMemo(() => numDeadline >= 1 * 24 * 60, [numDeadline]);

  const handleSlippage = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const { success } = slippageSchema.safeParse(Number(value));
    if (success || value == '') {
      const decimalParts = value.split('.');
      if (decimalParts.length > 1 && decimalParts[1].length > 2) return;
      setInputSlippage(value);
      setSlippage(Number(value || 3));
      setIsAutoSlippage(false);
    }
  };

  const handleDeadline = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace('.', '');
    const { success } = deadlineSchema.safeParse(Number(value));
    if (success || value == '') {
      setInputDeadline(value);
      setDeadline(Number(value));
    }
  };

  const handleAutoClick = () => {
    setIsAutoSlippage(true);
    setInputSlippage('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          'flex items-center space-x-1',
          isHighSlippage && 'text-[#F8712B]',
          isVeryHighSlippage && 'text-[#F95E5E]'
        )}
      >
        {OPEN_SLIPPAGE && (
          <>
            {isHighSlippage && <ViewWarnIcon className="size-3 fill-current" />}
            {isVeryHighSlippage && <ViewErrorIcon className="size-3 fill-current" />}
            {isAutoSlippage ? (
              <span className="text-xs leading-none font-medium text-beneficial">
                {autoSlippageDisplay > 0 ? `${autoSlippageDisplay.toFixed(1)}%` : t('auto')}
              </span>
            ) : (
              <span className="text-xs leading-none font-medium">{slippage}%</span>
            )}
          </>
        )}
        <InteractSettingsIcon className="size-4 fill-foreground/70 duration-200 hover:rotate-90" />
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={e => e.preventDefault()}
        align="end"
        className="space-y-6 w-full max-w-47.5 mt-2 p-5 -translate-y-2 translate-x-2 !rounded-4 bg-card border border-foreground/10"
      >
        {OPEN_SLIPPAGE && (
          <div className="flex space-y-3 flex-col pos-relative">
            <span className="text-sm font-medium leading-5 text-foreground/70 inline-flex items-center space-x-1">
              <span>{t('max-slippage')}</span>
              <TextTooltip show={open} tooltip={t('to-manage-risk-your-transaction-will-be-canceled-i')}>
                <ViewHitIcon className="size-3.5 fill-current cursor-pointer" />
              </TextTooltip>
            </span>
            <Input
              value={isAutoSlippage ? '' : inputSlippage}
              placeholder={isAutoSlippage ? (autoSlippageDisplay > 0 ? autoSlippageDisplay.toFixed(1) : t('auto')) : ''}
              onKeyDown={handleKeyDownDisableNumber}
              onChange={handleSlippage}
              className={cn(
                'text-base font-semibold h-8.5 py-1 leading-3.5 rounded-full text-right pr-6 !ring-none',
                !isHighSlippage && !isVeryHighSlippage && 'focus:border-beneficial',
                isHighSlippage && '!text-[#F8712B] border-[#F8712B]',
                isVeryHighSlippage && '!text-destructive border-destructive'
              )}
            />
            {isHighSlippage && (
              <div className="text-[#F8712B] text-3 leading-3 font-medium flex items-center space-x-1">
                <ViewWarnIcon className="size-3.5 fill-current" />
                {t('high-slippage')}
              </div>
            )}
            {isVeryHighSlippage && (
              <div className="text-destructive text-3 leading-3 font-medium flex items-center space-x-1">
                <ViewErrorIcon className="size-3.5 fill-current" />
                {t('very-high-slippage')}
              </div>
            )}
            <Button
              size="sm"
              variant="secondary"
              onClick={handleAutoClick}
              className={cn(
                'pos-absolute left-1 shadow-none top-5.75 h-7 px-4 rounded-full text-sm leading-3.5 font-semibold',
                isAutoSlippage ? 'bg-beneficial/20 text-beneficial-foreground' : 'text-foreground/40'
              )}
            >
              {t('auto')}
            </Button>
            <div className="pos-absolute right-2 top-6.7">
              <span className="text-sm font-medium leading-5 text-foreground/40">%</span>
            </div>
          </div>
        )}
        {!hideDeadline && (
          <div className="flex space-y-3 flex-col pos-relative">
            <span className="text-sm font-medium leading-5 text-foreground/70 inline-flex items-center space-x-1">
              <span>{t('trade-deadline')}</span>
              <TextTooltip
                show={open}
                tooltip={t('your-transaction-will-revert-if-it-is-pending-for-', { day: maxDeadline / 60 / 24 })}
              >
                <ViewHitIcon className="size-3.5 fill-current cursor-pointer" />
              </TextTooltip>
            </span>
            <Input
              value={inputDeadline}
              onKeyDown={handleKeyDownDisableNumber}
              onChange={handleDeadline}
              className={cn(
                'px-4 h-8.5 py-1 font-semibold leading-3.5 rounded-full pr-15.5 !ring-none',
                !isMaxDeadline && 'focus:border-beneficial',
                isMaxDeadline && '!text-destructive border-destructive'
              )}
            />
            {isMaxDeadline && (
              <div className="text-destructive text-3 leading-3 font-medium flex items-center space-x-1">
                <ViewErrorIcon className="size-3.5 fill-current" />
                {t('maximum-maxdeadline', { maxDeadline, day: maxDeadline / 60 / 24 })}
              </div>
            )}
            <div className="pos-absolute right-2 top-6.5">
              <span className="text-sm font-medium leading-5 text-foreground/40">{t('minutes')}</span>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
